// src/store/masterStatusStore.ts
import { create } from "zustand";
import { useUserStore } from "./userStore";
import {
  fetchReviewPartsWithManualStatus,
  updateManualStatus,
} from "../apiServices/reviewManualStatusApi";
import { updateWorkspaceStatus } from "../apiServices/partsCatalogStatusApi";
import type {
  MasterStatusDto,
  MasterStatusListDto,
  StatusOptionDto,
} from "../models/MasterStatus";

function resolveOem(): string {
  const s: any = useUserStore.getState();
  const legacy = s.currentOEM?.oemName as string | undefined;
  if (legacy && legacy.trim()) return legacy;
  const id = s.user?.oemId;
  if (id) return `Oem${id}`;
  const envDefault = (import.meta as any)?.env?.VITE_DEFAULT_OEM as string | undefined;
  return envDefault && envDefault.trim() ? envDefault : "Oem1";
}

function normalizeRoleForApi(role?: string | null): string {
  const key = String(role ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (!key) return "";
  if (key.includes("SUPERADMIN") || key === "ADMIN") return "SUPERADMIN";
  if (key.includes("OEM") && key.includes("REVIEWER")) return "OEMREVIEWER";
  if (key.includes("REVIEWER")) return "REVIEWER";
  if (key.includes("AUTHOR")) return "AUTHOR";
  if (key === "OEM") return "OEM";
  if (key.includes("VENDOR")) return "VENDOR";
  return key;
}

interface MasterStatusStore {
  items: MasterStatusDto[];
  statuses: StatusOptionDto[];
  loading: boolean;
  error: string | null;
  hasLoadedOnce: boolean;

  load: () => Promise<void>;
  /** Update part/workspace status (existing endpoint). */
  setPartStatus: (workspaceAssemblyId: number, statusId: number) => Promise<void>;
  /** Update one manual status (manual endpoint). */
  setManualStatus: (item: MasterStatusDto, manualId: number, statusId: number) => Promise<void>;

  clear: () => void;
}

export const useMasterStatusStore = create<MasterStatusStore>((set, get) => ({
  items: [],
  statuses: [],
  loading: false,
  error: null,
  hasLoadedOnce: false,

  async load() {
    const { accessToken, user } = useUserStore.getState();
    if (!accessToken) {
      set({ items: [], statuses: [], hasLoadedOnce: true, loading: false, error: null });
      return;
    }
    set({ loading: true, error: null });

    try {
      const oem = resolveOem();
      const role = normalizeRoleForApi(user?.role); // backend expects role to decide which list to send
      const data: MasterStatusListDto = await fetchReviewPartsWithManualStatus(oem, role);
      set({
        items: data.items ?? [],
        statuses: data.statuses ?? [],
        loading: false,
        hasLoadedOnce: true,
      });
    } catch (e: any) {
      set({
        items: [],
        statuses: [],
        loading: false,
        hasLoadedOnce: true,
        error: e?.message ?? "Failed to load review components",
      });
    }
  },

  async setPartStatus(workspaceAssemblyId, statusId) {
    const oem = resolveOem();
    const resp = await updateWorkspaceStatus(oem, workspaceAssemblyId, statusId);
    if (resp?.statusCode !== 200) {
      set({ error: resp?.message ?? "Failed to update part status." });
      return;
    }

    set((state) => {
      const statusName =
        state.statuses.find((s) => s.statusId === statusId)?.statusType ?? undefined;

      return {
        items: state.items.map((it) =>
          it.workspaceAssemblyId === workspaceAssemblyId
            ? {
                ...it,
                currentStatusId: statusId,
                currentStatus: statusName ?? it.currentStatus,
              }
            : it
        ),
        error: null,
      };
    });
  },

  async setManualStatus(item, manualId, statusId) {
    const oem = resolveOem();
    const targetStatus = get().statuses.find((s) => s.statusId === statusId)?.statusType?.trim();
    if (!targetStatus) {
      set({ error: "Unable to resolve target status." });
      return;
    }

    const manual = (item.manuals ?? []).find((m) => m.manualId === manualId);
    if (!manual) {
      set({ error: "Manual not found for this component." });
      return;
    }

    const user = useUserStore.getState().user;
    const updatedBy = user?.username || user?.emailId || "system";
    const updatedOn = new Date().toISOString();

    try {
      const resp = await updateManualStatus(oem, manualId, {
        status: targetStatus,
        updatedBy,
        updatedOn,
      });

      if (resp?.statusCode !== 200) {
        set({ error: resp?.message ?? "Failed to update manual status." });
        return;
      }

      const rowId = item.workspaceAssemblyId ?? item.masterAssemblyId;
      set((state) => ({
        items: state.items.map((it) => {
          const itRowId = it.workspaceAssemblyId ?? it.masterAssemblyId;
          if (itRowId !== rowId) return it;
          return {
            ...it,
            manuals: (it.manuals ?? []).map((manual) => {
              if (manual.manualId === manualId) {
                if (resp.data) return resp.data;
                return {
                  ...manual,
                  status: targetStatus,
                  updatedBy,
                  updatedOn,
                };
              }
              return manual;
            }),
          };
        }),
        error: null,
      }));
    } catch (e: any) {
      set({ error: e?.message ?? "Failed to update manual status." });
    }
  },

  clear: () =>
    set({
      items: [],
      statuses: [],
      loading: false,
      error: null,
      hasLoadedOnce: false,
    }),
}));
